import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  Typography,
  Alert,
  IconButton,
  Tooltip,
  Button,
} from "@mui/material";
import {
  TrendingUp,
  Web,
  Storage,
  Speed,
  Add,
  Refresh,
} from "@mui/icons-material";
import { dashboardAnalytics } from "../redux/slices/analyticsSlice";
import { fetchUsage } from "../redux/slices/usageSlice";
import Loader from "../components/Loader";
import { allSites } from "../redux/slices/siteSlice";
import StatsCard from "../components/dashboard/StatsCard";
import TrafficOverview from "../components/dashboard/TrafficOverview";
import QuickActionsCard from "../components/dashboard/QuickActionsCard";
import RecentSitesTable from "../components/dashboard/RecentSitesTable";
import RecentActivities from "../components/dashboard/RecentActivities";
import PendingInvitations from "../components/dashboard/PendingInvitations";
import { SPACING } from "../theme/constants";

const Dashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    analytics,
    loading: analyticsLoading,
    error: analyticsError,
  } = useSelector((state) => state.analytics);
  const {
    site: sitesState,
    loading: sitesLoading,
    error: sitesError,
  } = useSelector((state) => state.site);
  const { usage } = useSelector((state) => state.usage);

  useEffect(() => {
    dispatch(dashboardAnalytics());
    dispatch(allSites());
    dispatch(fetchUsage());
  }, [dispatch]);

  const loading = analyticsLoading || sitesLoading;

  // Format storage value: show as GB with plan limit if usage data available
  const formatStorage = () => {
    if (usage?.storage) {
      return `${usage.storage.usedGB} / ${usage.storage.limitGB} GB`;
    }
    const mb = analytics?.data?.overview?.totalStorageMB || 0;
    return mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${mb} MB`;
  };

  const getStorageSubtext = () => {
    if (usage?.storage) {
      return `${usage.storage.pct}% of plan limit`;
    }
    return "total across sites";
  };

  const statsData = analytics
    ? [
        {
          title: "Total Sites",
          value: usage
            ? `${usage.sites.used} / ${usage.sites.limit}`
            : analytics?.data?.overview?.totalSites,
          change: usage
            ? `${usage.sites.pct}% of plan limit`
            : `${analytics?.data?.overview?.activeSites} active`,
          icon: <Web />,
          color: "#0073e6",
          bgColor: "#e3f2fd",
        },
        {
          title: "Storage Used",
          value: formatStorage(),
          change: getStorageSubtext(),
          icon: <Storage />,
          color: "#00c9a7",
          bgColor: "#e0f7f4",
        },
        {
          title: "Backups",
          value: analytics?.data?.overview?.totalBackups,
          change: analytics?.data?.overview?.totalBackups
            ? "Total"
            : "No backups yet",
          icon: <Speed />,
          color: "#ff9800",
          bgColor: "#fff3e0",
        },
        {
          title: "Recent Deployments",
          value: analytics?.data?.overview?.recentDeployments,
          change: "this week",
          icon: <TrendingUp />,
          color: "#f44336",
          bgColor: "#ffebee",
        },
      ]
    : [];

  if (loading) {
    return <Loader />;
  }

  return (
    <Box>
      {/* Pending Invitations Banner */}
      <PendingInvitations />

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: SPACING.lg,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: SPACING.xs }}>
            Dashboard Overview
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Monitor your WordPress sites performance and health
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton
              size="small"
              onClick={() => {
                dispatch(dashboardAnalytics());
                dispatch(allSites());
                dispatch(fetchUsage());
              }}
              disabled={loading}
            >
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate("/dashboard/sites")}
            disableElevation
            sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
          >
            New Site
          </Button>
        </Box>
      </Box>

      {analyticsError && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          Failed to load analytics. {analyticsError?.message || ""}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={1} sx={{ mb: SPACING.md }}>
        {statsData.map((stat, index) => (
          <Grid key={index} size={{ xs: 12, sm: 6 }}>
            <StatsCard stat={stat} />
          </Grid>
        ))}
      </Grid>

      {/* Traffic Overview & Quick Actions */}
      <Grid container spacing={SPACING.md}>
        {/* Traffic Chart */}
        <Grid size={{ xs: 12, md: 8 }} sx={{ display: "flex" }}>
          <TrafficOverview />
        </Grid>

        {/* Quick Actions */}
        <Grid size={{ xs: 12, md: 4 }} sx={{ display: "flex" }}>
          <QuickActionsCard />
        </Grid>

        {/* Recent Sites */}
        <Grid size={{ xs: 12, lg: 8 }} sx={{ display: "flex" }}>
          <RecentSitesTable
            sites={sitesState?.sites}
            loading={sitesLoading}
            error={sitesError}
          />
        </Grid>

        {/* Recent Activities */}
        <Grid size={{ xs: 12, lg: 4 }} sx={{ display: "flex" }}>
          <RecentActivities activities={analytics?.data?.recentActivities} />
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
